
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  X, Calendar, CheckCircle2, Edit2, Save, Trash2, MessageSquare, Send, Download, File, Trello, Plus, AtSign, Loader2, Paperclip, Clock, History, Briefcase, AlignLeft, Archive, AlertTriangle, Check, ListChecks, UserCheck, Users, Info, RotateCcw, Tag, CheckSquare, Square
} from 'lucide-react';
import { Task, TaskPriority, TaskStatus, TaskHistory, Contact, TaskSector, TaskComment, Attachment, User, ChecklistItem, UserRole, WhatsAppAssessor } from '../types';
import { supabase } from '../supabase';
import SearchableSelect from './SearchableSelect';
import MentionInput from './MentionInput';
import FormattedText from './FormattedText';
import { useDraftState } from '../hooks/useDraftState';
import { extractMentionedUserIds } from '../utils/mentions';

interface TaskDetailModalProps {
  task: Task;
  currentUser: User;
  users: User[];
  waAssessors?: WhatsAppAssessor[];
  onClose: () => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onDeleteTask: (id: string) => Promise<boolean>;
  contacts: Contact[];
  sectors: TaskSector[];
}

const normalizeUuid = (val: string | null | undefined) => val && val.trim().length > 0 ? val : null;

// Helper para parse seguro
const parseAttachment = (att: any): Attachment => {
  if (typeof att === 'string') {
    try {
      return JSON.parse(att);
    } catch (e) {
      console.error("Erro ao fazer parse do anexo:", att);
      return { id: 'error', name: 'Erro no arquivo', url: '#', timestamp: new Date().toISOString() };
    }
  }
  return att;
};

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ task, currentUser, users, waAssessors = [], onClose, onUpdateTask, onDeleteTask, contacts, sectors }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [checkInput, setCheckInput] = useState('');

  // Rascunho Persistente para Edição
  const [editedFields, setEditedFields, clearDraft] = useDraftState(`edit_task_${task.id}`, {
    title: task.title, 
    description: task.description || '', 
    sector: task.sector, 
    priority: task.priority,
    duedate: task.duedate || '', 
    assignee_id: task.assignees[0] || '',
    stakeholders: task.stakeholders || [],
    contactid: task.contactid || '',
    status: task.status,
    tags: task.tags || [],
    checklist: task.checklist || [],
    attachments: (task.attachments || []).map(parseAttachment)
  });

  const taskResponsible = useMemo(() => {
    const assigneeId = task.assignees[0];
    if (!assigneeId) return null;
    if (assigneeId.startsWith('wa_')) {
      const wa = waAssessors.find(a => `wa_${a.id}` === assigneeId);
      return wa ? { name: wa.name, phone: wa.phone, isWa: true } : null;
    }
    const u = users.find(u => u.id === assigneeId);
    return u ? { name: u.name, phone: u.phone, isWa: false } : null;
  }, [task.assignees, users, waAssessors]);
  const taskRequester = useMemo(() => contacts.find(c => c.id === task.contactid), [task.contactid, contacts]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    const comment: TaskComment = { id: `c-${Date.now()}`, userId: currentUser.id, timestamp: new Date().toISOString(), text: newComment.trim() };
    try {
      await onUpdateTask(task.id, { comments: [...(task.comments || []), comment] });
      const mentionedIds = extractMentionedUserIds(newComment);
      const uniqueMentions = mentionedIds.filter(id => id !== currentUser.id);
      if (uniqueMentions.length > 0) {
        const notifications = uniqueMentions.map(userId => ({
          user_id: userId, type: 'mention', title: 'Mencionaram você',
          description: `${currentUser.name} mencionou você na tarefa: "${task.title}"`,
          related_id: task.id, read: false
        }));
        await supabase.from('notifications').insert(notifications);
      }
      setNewComment('');
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (uploading) return;
    setSaving(true);
    setErrorMsg(null);
    const payload = {
      title: editedFields.title, 
      description: editedFields.description, 
      sector: editedFields.sector,
      priority: editedFields.priority, 
      duedate: editedFields.duedate || null,
      assignees: [editedFields.assignee_id],
      stakeholders: editedFields.stakeholders || [],
      contactid: normalizeUuid(editedFields.contactid),
      status: editedFields.status,
      tags: editedFields.tags,
      checklist: editedFields.checklist,
      attachments: editedFields.attachments, 
      history: [...(task.history || []), { id: `h-edt-${Date.now()}`, userId: currentUser.id, timestamp: new Date().toISOString(), action: 'Atualizou informações da tarefa' }]
    };
    try {
      await onUpdateTask(task.id, payload);

      // WhatsApp notifications
      clearDraft();
      setIsEditing(false);
    } catch (err: any) { setErrorMsg("Falha ao salvar as alterações."); } finally { setSaving(false); }
  };

  const toggleCheckItem = async (itemId: string) => {
    const newChecklist = (task.checklist || []).map(item => 
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    onUpdateTask(task.id, { checklist: newChecklist });
  };

  const addTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim().replace(',', '');
      if (val && !editedFields.tags.includes(val)) {
        setEditedFields({ ...editedFields, tags: [...editedFields.tags, val] });
        setTagInput('');
      }
    }
  };

  const addChecklistItem = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && checkInput.trim()) {
      e.preventDefault();
      const newItem: ChecklistItem = { id: `cl-edt-${Date.now()}`, text: checkInput.trim(), completed: false };
      setEditedFields({ ...editedFields, checklist: [...editedFields.checklist, newItem] });
      setCheckInput('');
    }
  };

  // Upload Real para Supabase Storage
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    
    setUploading(true);
    try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `${currentUser.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('cabinet-files')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from('cabinet-files').getPublicUrl(filePath);

        const newAttachment: Attachment = {
            id: `att-${Date.now()}`,
            name: file.name,
            url: data.publicUrl,
            type: file.type.includes('image') ? 'image' : 'file',
            size: file.size,
            timestamp: new Date().toISOString()
        };
        
        setEditedFields((prev: any) => ({ ...prev, attachments: [...(prev.attachments || []), newAttachment] }));
    } catch (error: any) {
        setErrorMsg('Erro ao fazer upload: ' + error.message);
    } finally {
        setUploading(false);
        e.target.value = '';
    }
  };

  const removeAttachment = (id: string) => {
    setEditedFields((prev: any) => ({ 
        ...prev, 
        attachments: (prev.attachments || []).filter((a: Attachment) => a.id !== id) 
    }));
  };

  const priorityStyles = {
    [TaskPriority.LOW]: 'text-slate-400 bg-slate-50 border-slate-100',
    [TaskPriority.MEDIUM]: 'text-parliament-600 bg-parliament-50 border-parliament-100',
    [TaskPriority.HIGH]: 'text-orange-500 bg-orange-50 border-orange-100',
    [TaskPriority.URGENT]: 'text-red-500 bg-red-50 border-red-100',
  };

  // Garante que a lista de visualização também esteja limpa
  const viewAttachments = (task.attachments || []).map(parseAttachment);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-4xl max-h-[90vh] rounded-[28px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative" onClick={e => e.stopPropagation()}>

        {showDeleteConfirm && (
          <div className="absolute inset-0 z-[60] bg-navy-950/90 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center animate-in fade-in">
             <Trash2 size={48} className="text-red-500 mb-4" />
             <h2 className="text-2xl font-black text-white mb-4 uppercase">Remover Tarefa?</h2>
             <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)} className="px-8 py-3 bg-white/10 text-white rounded-xl font-black uppercase text-xs">Cancelar</button>
                <button onClick={async () => { await onDeleteTask(task.id); onClose(); }} className="px-8 py-3 bg-red-600 text-white rounded-xl font-black uppercase text-xs">Confirmar</button>
             </div>
          </div>
        )}

        <div className="px-6 py-4 border-b border-slate-100 dark:border-navy-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-navy-950 text-white shadow-lg"><Trello size={20} /></div>
            <h2 className="text-lg font-black text-slate-800 dark:text-white tracking-tight">{isEditing ? 'Configurações da Tarefa' : 'Detalhamento Operacional'}</h2>
          </div>
          <div className="flex items-center gap-2">
             {!isEditing && currentUser.permissions.canEditTasks && (
                <>
                  <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-5 py-2 bg-neutral-100 dark:bg-navy-800 text-neutral-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase shadow-sm hover:bg-navy-950 hover:text-white transition-all"><Edit2 size={14}/> Editar</button>
                  <button onClick={() => setShowDeleteConfirm(true)} className="p-2 text-slate-300 hover:text-red-500 transition-all"><Trash2 size={18}/></button>
                </>
             )}
             <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full text-neutral-400"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row bg-slate-50/20 dark:bg-navy-950/20">
          <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-6 space-y-6">
            {isEditing ? (
              <div className="space-y-10 animate-in slide-in-from-left-4">
                 {errorMsg && <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold flex items-center gap-2"><AlertTriangle size={16}/> {errorMsg}</div>}
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Assunto / Título</label>
                    <input value={editedFields.title} onChange={e => setEditedFields({...editedFields, title: e.target.value})} className="w-full text-3xl font-black bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-700 rounded-2xl px-6 py-5 shadow-inner outline-none dark:text-white focus:ring-4 focus:ring-parliament-50" />
                 </div>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Responsável</label>
                       <select value={editedFields.assignee_id} onChange={e => setEditedFields({...editedFields, assignee_id: e.target.value})} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white">
                         <optgroup label="Equipe">
                           {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.phone ? ` • ${u.phone}` : ''}</option>)}
                         </optgroup>
                         {waAssessors.length > 0 && (
                           <optgroup label="Assessores WhatsApp">
                             {waAssessors.map(a => <option key={`wa_${a.id}`} value={`wa_${a.id}`}>{a.name} • {a.phone}</option>)}
                           </optgroup>
                         )}
                       </select>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Solicitante (Cidadão)</label>
                       <SearchableSelect options={contacts.map(c => ({ value: c.id, label: c.name }))} value={editedFields.contactid} onChange={val => setEditedFields({...editedFields, contactid: val})} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" />
                    </div>
                 </div>

                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Stakeholders (informados sobre andamento)</label>
                    <div className="bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl p-4 max-h-36 overflow-y-auto space-y-1">
                      {users.filter(u => u.id !== editedFields.assignee_id).map(u => (
                        <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-navy-700 px-2 py-1.5 rounded-lg">
                          <input type="checkbox" checked={editedFields.stakeholders?.includes(u.id) || false} onChange={e => {
                            const current = editedFields.stakeholders || [];
                            setEditedFields({...editedFields, stakeholders: e.target.checked ? [...current, u.id] : current.filter((id: string) => id !== u.id)});
                          }} className="rounded" />
                          <span className="text-sm font-bold dark:text-white">{u.name}</span>
                          {u.phone && <span className="text-[10px] text-neutral-400">{u.phone}</span>}
                        </label>
                      ))}
                      {waAssessors.filter(a => `wa_${a.id}` !== editedFields.assignee_id).map(a => (
                        <label key={`wa_${a.id}`} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-navy-700 px-2 py-1.5 rounded-lg">
                          <input type="checkbox" checked={editedFields.stakeholders?.includes(`wa_${a.id}`) || false} onChange={e => {
                            const current = editedFields.stakeholders || [];
                            setEditedFields({...editedFields, stakeholders: e.target.checked ? [...current, `wa_${a.id}`] : current.filter((id: string) => id !== `wa_${a.id}`)});
                          }} className="rounded" />
                          <span className="text-sm font-bold dark:text-white">{a.name}</span>
                          <span className="text-[10px] text-emerald-600">{a.phone}</span>
                        </label>
                      ))}
                    </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Setor</label>
                       <select value={editedFields.sector} onChange={e => setEditedFields({...editedFields, sector: e.target.value})} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl px-4 py-4 text-sm font-bold dark:text-white">
                         {sectors.map(s => <option key={s} value={s}>{s}</option>)}
                         {!sectors.includes(editedFields.sector) && (
                            <option value={editedFields.sector} disabled>{editedFields.sector} (Desativado)</option>
                         )}
                       </select>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Prioridade</label>
                       <select value={editedFields.priority} onChange={e => setEditedFields({...editedFields, priority: e.target.value as TaskPriority})} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl px-4 py-4 text-sm font-bold dark:text-white">
                         {Object.values(TaskPriority).map(p => <option key={p} value={p}>{p}</option>)}
                       </select>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Prazo Final</label>
                       <input type="date" value={editedFields.duedate} onChange={e => setEditedFields({...editedFields, duedate: e.target.value})} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl px-4 py-4 text-sm font-bold dark:text-white" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Status</label>
                       <select value={editedFields.status} onChange={e => setEditedFields({...editedFields, status: e.target.value as TaskStatus})} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl px-4 py-4 text-sm font-bold dark:text-white">
                         {Object.values(TaskStatus).map(s => <option key={s} value={s}>{s}</option>)}
                       </select>
                    </div>
                 </div>

                 {/* Anexos (Restaurado) */}
                 <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Paperclip size={14}/> Anexos</label>
                        <label className="cursor-pointer text-[10px] font-bold text-parliament-600 uppercase hover:underline flex items-center gap-1">
                           {uploading ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>} 
                           {uploading ? 'Enviando...' : 'Adicionar Arquivo'}
                           <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                        </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {editedFields.attachments && editedFields.attachments.length > 0 ? (
                            editedFields.attachments.map((rawAtt: any) => {
                                const att = parseAttachment(rawAtt);
                                return (
                                    <div key={att.id} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-700 rounded-xl shadow-sm">
                                        <File size={14} className="text-slate-400" />
                                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate max-w-[150px]">{att.name}</span>
                                        <button onClick={() => removeAttachment(att.id)} className="text-slate-400 hover:text-red-500"><X size={12}/></button>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-xs text-slate-400 italic pl-1">Nenhum anexo.</p>
                        )}
                    </div>
                 </div>

                 <div className="space-y-6">
                    <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 border-l-4 border-emerald-500 pl-3">Checklist Operacional</p>
                    <div className="bg-white dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-[32px] p-8 space-y-4 shadow-inner">
                        <div className="flex gap-3">
                          <input value={checkInput} onChange={e => setCheckInput(e.target.value)} onKeyDown={addChecklistItem} placeholder="Adicionar passo..." className="flex-1 bg-neutral-50 dark:bg-navy-900 border border-neutral-100 dark:border-navy-700 rounded-xl px-5 py-3 text-xs font-bold dark:text-white outline-none" />
                          <button type="button" onClick={() => addChecklistItem({ key: 'Enter', preventDefault: () => {} } as any)} className="p-3 bg-emerald-500 text-white rounded-xl shadow-lg"><Plus size={18}/></button>
                        </div>
                        <div className="space-y-2">
                          {editedFields.checklist.map((item, idx) => (
                            <div key={item.id} className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-navy-900 rounded-xl border border-transparent">
                                <div className="flex items-center gap-3"><Square size={14} className="text-slate-300" /><span className="text-xs font-bold text-slate-600 dark:text-slate-300">{item.text}</span></div>
                                <button type="button" onClick={() => setEditedFields({...editedFields, checklist: editedFields.checklist.filter((_, i) => i !== idx)})} className="text-slate-300 hover:text-red-500"><X size={14}/></button>
                            </div>
                          ))}
                        </div>
                    </div>
                 </div>

                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Descrição</label>
                    <textarea value={editedFields.description} onChange={e => setEditedFields({...editedFields, description: e.target.value})} rows={5} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-3xl p-8 outline-none dark:text-white text-lg font-medium shadow-inner" />
                 </div>

                 <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Tag size={14} /> Etiquetas</label>
                    <div className="flex flex-wrap gap-2 p-4 bg-white dark:bg-navy-900 border border-neutral-100 rounded-2xl shadow-sm">
                      {editedFields.tags.map(tag => (
                        <span key={tag} className="flex items-center gap-2 px-3 py-1.5 bg-navy-50 dark:bg-navy-700 text-navy-900 dark:text-navy-200 rounded-lg text-[9px] font-black uppercase border border-navy-100 dark:border-navy-600">{tag}<button type="button" onClick={() => setEditedFields({...editedFields, tags: editedFields.tags.filter(t => t !== tag)})} className="hover:text-red-500"><X size={10} /></button></span>
                      ))}
                      <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={addTag} placeholder="Add tag..." className="flex-1 bg-transparent border-none outline-none text-xs font-bold min-w-[250px] dark:text-white" />
                    </div>
                 </div>

                 <div className="flex justify-end gap-3 pt-6">
                    <button onClick={() => { setIsEditing(false); clearDraft(); }} className="px-8 py-3 font-bold text-slate-400">Descartar</button>
                    <button onClick={handleSave} disabled={saving || uploading} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-[11px] uppercase tracking-widest shadow-2xl flex items-center gap-3 disabled:opacity-50">
                      {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} SALVAR ALTERAÇÕES
                    </button>
                 </div>
              </div>
            ) : (
              <div className="space-y-12 animate-in fade-in">
                <div className="space-y-4">
                   <div className="flex items-center gap-3">
                      <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${priorityStyles[task.priority]}`}>Prioridade {task.priority}</span>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Tag size={12}/> {task.sector.toUpperCase()}</span>
                      <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-500`}>{task.status}</span>
                   </div>
                   <h1 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter leading-tight">{task.title}</h1>
                   <div className="flex flex-wrap gap-2 mt-4">
                      {(task.tags || []).map(tag => (
                        <span key={tag} className="px-3 py-1 bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-700 rounded-lg text-[9px] font-black uppercase text-slate-500 tracking-widest">{tag}</span>
                      ))}
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                   <div className="bg-white dark:bg-navy-800 p-8 rounded-[32px] border border-slate-100 dark:border-navy-700 shadow-sm space-y-4">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Responsável Atribuído</p>
                      <div className="flex items-center gap-4">
                         <div className={`w-14 h-14 rounded-[20px] ${taskResponsible?.isWa ? 'bg-emerald-600' : 'bg-navy-950'} text-white flex items-center justify-center font-black text-xl shadow-lg`}>{taskResponsible?.name.charAt(0) || '?'}</div>
                         <div>
                           <p className="font-bold text-navy-950 dark:text-white text-lg">{taskResponsible?.name || 'Não atribuído'}</p>
                           <p className="text-xs font-bold text-parliament-600 uppercase">{taskResponsible?.isWa ? 'WhatsApp' : 'Assessor'}{taskResponsible?.phone ? ` • ${taskResponsible.phone}` : ''}</p>
                         </div>
                      </div>
                   </div>
                   <div className="bg-white dark:bg-navy-800 p-8 rounded-[32px] border border-slate-100 dark:border-navy-700 shadow-sm space-y-4">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Prazos e Cronograma</p>
                      <div className="flex items-center gap-4">
                         <div className="w-14 h-14 rounded-[20px] bg-slate-50 dark:bg-navy-700 text-slate-400 flex items-center justify-center shadow-inner"><Calendar size={24} /></div>
                         <div><p className="font-black text-navy-950 dark:text-white text-lg">{task.duedate ? new Date(task.duedate + 'T12:00:00').toLocaleDateString('pt-BR') : 'Sem prazo'}</p><p className="text-xs font-bold text-slate-400 uppercase">Data de Entrega</p></div>
                      </div>
                   </div>
                   {taskRequester && (
                     <div className="bg-white dark:bg-navy-800 p-8 rounded-[32px] border border-slate-100 dark:border-navy-700 shadow-sm space-y-4">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Solicitante (CRM)</p>
                        <div className="flex items-center gap-4">
                           <div className="w-14 h-14 rounded-[20px] bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 flex items-center justify-center shadow-inner"><UserCheck size={24} /></div>
                           <div><p className="font-bold text-navy-950 dark:text-white text-lg">{taskRequester.name}</p><p className="text-xs font-bold text-emerald-500 uppercase">{taskRequester.type}</p></div>
                        </div>
                     </div>
                   )}
                </div>
                {task.stakeholders && task.stakeholders.length > 0 && (
                  <div className="bg-white dark:bg-navy-800 p-6 rounded-[32px] border border-slate-100 dark:border-navy-700 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Stakeholders</p>
                    <div className="flex flex-wrap gap-2">
                      {task.stakeholders.map(sid => {
                        const isWa = sid.startsWith('wa_');
                        const person = isWa
                          ? waAssessors.find(a => `wa_${a.id}` === sid)
                          : users.find(u => u.id === sid);
                        if (!person) return null;
                        return (
                          <div key={sid} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-navy-700 rounded-xl">
                            <div className={`w-8 h-8 rounded-lg ${isWa ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-parliament-100 dark:bg-parliament-900/30 text-parliament-600'} flex items-center justify-center font-black text-xs`}>
                              {person.name.charAt(0)}
                            </div>
                            <div>
                              <p className="text-xs font-bold text-navy-950 dark:text-white">{person.name}</p>
                              <p className="text-[9px] text-neutral-400">{isWa ? 'WhatsApp' : 'Equipe'}{person.phone ? ` • ${person.phone}` : ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Anexos (Visualização) */}
                {viewAttachments.length > 0 && (
                    <div className="space-y-6">
                        <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3"><Paperclip size={24} className="text-slate-400"/> Anexos</h3>
                        <div className="flex flex-wrap gap-4">
                            {viewAttachments.map((att: Attachment) => (
                                <div key={att.id} className="flex items-center gap-3 p-4 bg-white dark:bg-navy-900 border border-slate-100 dark:border-navy-800 rounded-2xl shadow-sm">
                                    <div className="p-2 bg-slate-50 dark:bg-navy-800 rounded-lg text-slate-400"><File size={20}/></div>
                                    <div>
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300 max-w-[200px] truncate" title={att.name}>{att.name || 'Sem nome'}</p>
                                        <p className="text-[10px] text-slate-400">{att.timestamp ? new Date(att.timestamp).toLocaleDateString() : '-'}</p>
                                    </div>
                                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-300 hover:text-parliament-600"><Download size={16}/></a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                  <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3"><AlignLeft size={24} className="text-parliament-600"/> Descrição da Demanda</h3>
                  <div className="p-8 bg-white dark:bg-navy-950 border border-slate-100 dark:border-navy-800 rounded-[32px] shadow-inner">
                    <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed font-medium">{task.description || "Sem detalhes adicionais registrados."}</p>
                  </div>
                </div>

                {/* VISUALIZAÇÃO CHECKLIST */}
                {task.checklist && task.checklist.length > 0 && (
                   <div className="space-y-6">
                      <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3"><ListChecks size={24} className="text-emerald-500"/> Checklist de Execução</h3>
                      <div className="bg-white dark:bg-navy-900 border border-neutral-100 dark:border-navy-800 rounded-[32px] p-8 space-y-3">
                         {task.checklist.map(item => (
                            <button key={item.id} onClick={() => toggleCheckItem(item.id)} className="w-full flex items-center gap-4 p-4 hover:bg-neutral-50 dark:hover:bg-navy-950 rounded-2xl transition-all border border-transparent hover:border-neutral-100 text-left group">
                               {item.completed ? <CheckSquare size={22} className="text-emerald-500" /> : <Square size={22} className="text-slate-300 group-hover:text-emerald-500" />}
                               <span className={`text-sm font-bold ${item.completed ? 'text-slate-400 line-through' : 'text-slate-700 dark:text-slate-200'}`}>{item.text}</span>
                            </button>
                         ))}
                      </div>
                   </div>
                )}

                <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-navy-800">
                  <h3 className="text-sm font-black dark:text-white flex items-center gap-2"><MessageSquare size={16} className="text-parliament-600" /> Notas e Comentários</h3>
                  <div className="space-y-4">
                     {task.comments && task.comments.length > 0 ? task.comments.map(c => {
                       const author = users.find(u => u.id === c.userId);
                       return (
                         <div key={c.id} className="p-5 bg-white dark:bg-navy-800 rounded-3xl border border-slate-100 dark:border-navy-700 shadow-sm">
                            <div className="flex justify-between mb-2">
                               <span className="text-[10px] font-black text-parliament-600 uppercase tracking-widest">{author?.name || 'Sistema'}</span>
                               <span className="text-[9px] font-bold text-slate-400">{new Date(c.timestamp).toLocaleString('pt-BR')}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-300"><FormattedText text={c.text} /></p>
                         </div>
                       );
                     }) : <p className="text-center py-6 text-slate-400 text-xs italic">Nenhum comentário registrado.</p>}
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 dark:bg-navy-800 text-slate-400 flex items-center justify-center shrink-0"><MessageSquare size={18}/></div>
                    <div className="flex-1 relative">
                      <MentionInput 
                        value={newComment} onChange={setNewComment} users={users} placeholder="Adicionar um comentário..." className="w-full bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-800 rounded-3xl px-6 py-4 outline-none focus:ring-4 focus:ring-parliament-50 dark:text-white shadow-inner resize-none text-sm font-medium transition-all"
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                      />
                      <button onClick={handleAddComment} disabled={!newComment.trim()} className="absolute right-4 bottom-4 p-2.5 bg-navy-950 dark:bg-parliament-600 text-white rounded-xl hover:scale-105 transition-all shadow-lg disabled:opacity-50"><Send size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
